/**
 * Recording an expense (P1-TR3, NFR-1, NFR-2, BR-06).
 *
 * The design constraint is §9.5: a partner standing in a feed shop, holding
 * their phone in one hand, records a ৳12,000 purchase with a photo in under
 * sixty seconds. Everything here follows from that.
 *
 *   The amount comes first and fills the screen, because it is the one field
 *   nobody can skip and the one a mistake matters most in.
 *
 *   A custom keypad rather than a text input. The native keyboard on a
 *   mid-range Android takes half the screen, arrives late, and offers a comma
 *   key that produces an amount the server rejects.
 *
 *   Categories are chips, not a select. A dropdown on a phone is two taps and
 *   a scroll; a chip is one tap and the common ones are visible without it.
 *
 *   The receipt rule is enforced here as well as on the server (BR-06), so a
 *   partner learns about it before they have typed everything and lost the
 *   photo opportunity. The server is still authoritative — this is courtesy,
 *   not enforcement (SEC-3).
 *
 *   Nothing is cleared until the server has confirmed it (NFR-2). On a rural
 *   connection a failed submit is ordinary, and losing the entry is the one
 *   outcome that sends a partner back to paper.
 */

import { toMoney } from '@agrofarm/shared';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from '../../i18n/useTranslation.js';
import {
  AMOUNT_KEYS,
  appendAmountKey,
  deleteAmountKey,
  formatAmountDraft,
  isCompleteAmount,
  toApiAmount,
  type AmountKey,
} from '../../lib/amountInput.js';
import { errorFields, errorMessageKey } from '../../lib/apiError.js';
import { todayInDhaka } from '../../lib/date.js';
import { useAccounts, useCategories, useCreateTransaction, useSettings, uploadReceipt } from './api.js';

interface Props {
  /** Called once the server has accepted the entry. */
  onRecorded?: (id: number) => void;
}

/** Fields this form marks next to the control itself; everything else the
 *  server rejects is shown in the summary, so no rejection is invisible. */
const INLINE_FIELD_ERRORS = ['categoryId'];

export function ExpenseForm({ onRecorded }: Props): JSX.Element {
  const { t } = useTranslation();

  const { data: categories = [] } = useCategories();
  const { data: accounts = [] } = useAccounts();
  const { data: settings } = useSettings();
  const createTransaction = useCreateTransaction();

  const [amount, setAmount] = useState('');
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [accountId, setAccountId] = useState<number | null>(null);
  const [note, setNote] = useState('');
  const [photo, setPhoto] = useState<File | null>(null);
  /**
   * Kept across a failed submit so a retry does not re-upload the photo — five
   * megabytes twice on a 3G connection is the difference between a partner
   * retrying and giving up.
   */
  const [receiptId, setReceiptId] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [savedId, setSavedId] = useState<number | null>(null);

  const fileInput = useRef<HTMLInputElement>(null);

  // The first cash account, preselected. Most farm spending is cash, and a
  // preselected default is one tap fewer for the common case.
  useEffect(() => {
    if (accountId === null && accounts.length > 0) {
      setAccountId((accounts.find((account) => account.type === 'cash') ?? accounts[0])!.id);
    }
  }, [accounts, accountId]);

  const threshold = settings?.receiptRequiredAbove ?? '500.00';

  /**
   * BR-06 — a receipt is required at or above the threshold.
   *
   * Compared as Decimals, never as numbers: this is a money comparison and it
   * decides whether an entry is allowed.
   */
  const receiptRequired = useMemo(
    () => isCompleteAmount(amount) && toMoney(toApiAmount(amount)).greaterThanOrEqualTo(toMoney(threshold)),
    [amount, threshold],
  );

  const missingReceipt = receiptRequired && receiptId === null;
  const canSubmit =
    isCompleteAmount(amount) && categoryId !== null && !missingReceipt && !isUploading && !createTransaction.isPending;

  function pressKey(key: AmountKey): void {
    setAmount((current) => appendAmountKey(current, key));
    setErrorKey(null);
  }

  async function handlePhoto(file: File): Promise<void> {
    setPhoto(file);
    setIsUploading(true);
    setErrorKey(null);

    try {
      setReceiptId(await uploadReceipt(file));
    } catch (error) {
      // The photo is still on the phone; only the upload failed. Keeping the
      // file selected means "try again" is one tap, not a second trip to the
      // camera.
      setErrorKey(errorMessageKey(error));
      setReceiptId(null);
    } finally {
      setIsUploading(false);
    }
  }

  async function handleSubmit(): Promise<void> {
    setErrorKey(null);
    setFieldErrors({});

    try {
      const created = await createTransaction.mutateAsync({
        txnDate: todayInDhaka(),
        type: 'expense',
        // A string all the way to the wire (MONEY-1, MONEY-2).
        amount: toApiAmount(amount),
        ...(categoryId !== null ? { categoryId } : {}),
        ...(accountId !== null ? { accountId } : {}),
        ...(note.trim() !== '' ? { note: note.trim() } : {}),
        ...(receiptId !== null ? { receiptUrl: receiptId } : {}),
      });

      setSavedId(created.id);
      onRecorded?.(created.id);

      // Only now is anything cleared.
      setAmount('');
      setCategoryId(null);
      setNote('');
      setPhoto(null);
      setReceiptId(null);
    } catch (error) {
      // Everything the partner typed is still on screen; submit is the retry.
      setErrorKey(errorMessageKey(error));
      setFieldErrors(errorFields(error) ?? {});
    }
  }

  return (
    <form
      className="page-container"
      onSubmit={(event) => {
        event.preventDefault();
        void handleSubmit();
      }}
      noValidate
    >
      <h1 className="py-4 text-xl font-semibold text-gray-800">{t('finance.expense.title')}</h1>

      {/*
        The amount, big enough to check at arm's length in a noisy shop.

        A div rather than an <output>: <output> carries an implicit `status`
        role, which would put the running amount in the same live region as the
        "saved" confirmation and make a screen reader announce every keypress
        as a status change. `aria-live="polite"` announces the total without
        claiming to be a status message.
      */}
      <div
        aria-label={t('finance.expense.amount')}
        aria-live="polite"
        className="mb-1 block w-full rounded-xl bg-white px-4 py-5 text-right text-4xl font-semibold tabular-nums text-gray-900 shadow-sm"
      >
        ৳ {formatAmountDraft(amount)}
      </div>

      {receiptRequired && (
        <p className={`mb-3 text-sm ${missingReceipt ? 'text-amber-700' : 'text-green-700'}`}>
          {t(missingReceipt ? 'finance.expense.receiptRequired' : 'finance.expense.receiptAttached', {
            amount: threshold,
          })}
        </p>
      )}

      <div className="mb-5 grid grid-cols-3 gap-2" role="group" aria-label={t('finance.expense.keypad')}>
        {AMOUNT_KEYS.map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => pressKey(key)}
            className="min-h-[56px] rounded-xl bg-white text-2xl font-medium text-gray-800 shadow-sm active:bg-gray-100"
          >
            {key}
          </button>
        ))}
        <button
          type="button"
          onClick={() => setAmount(deleteAmountKey)}
          aria-label={t('finance.expense.backspace')}
          className="min-h-[56px] rounded-xl bg-white text-2xl text-gray-600 shadow-sm active:bg-gray-100"
        >
          ⌫
        </button>
      </div>

      <fieldset className="mb-5">
        <legend className="mb-2 text-sm font-medium text-gray-700">{t('finance.expense.category')}</legend>
        <div className="flex flex-wrap gap-2">
          {categories
            .flatMap((parent) => [parent, ...(parent.children ?? [])])
            .map((category) => (
              <button
                key={category.id}
                type="button"
                onClick={() => setCategoryId(category.id)}
                aria-pressed={categoryId === category.id}
                className={`min-h-touch rounded-full px-4 py-2 text-sm ${
                  categoryId === category.id
                    ? 'bg-green-700 font-medium text-white'
                    : 'bg-white text-gray-700 shadow-sm'
                }`}
              >
                {category.name}
              </button>
            ))}
        </div>
        {fieldErrors['categoryId'] !== undefined && (
          <p className="mt-2 text-sm text-red-700">{fieldErrors['categoryId']}</p>
        )}
      </fieldset>

      <fieldset className="mb-5">
        <legend className="mb-2 text-sm font-medium text-gray-700">{t('finance.expense.paidFrom')}</legend>
        <div className="flex flex-wrap gap-2">
          {accounts.map((account) => (
            <button
              key={account.id}
              type="button"
              onClick={() => setAccountId(account.id)}
              aria-pressed={accountId === account.id}
              className={`min-h-touch rounded-full px-4 py-2 text-sm ${
                accountId === account.id ? 'bg-green-700 font-medium text-white' : 'bg-white text-gray-700 shadow-sm'
              }`}
            >
              {account.name}
            </button>
          ))}
        </div>
      </fieldset>

      <div className="mb-5">
        <input
          ref={fileInput}
          type="file"
          accept="image/*"
          // Opens the camera directly on a phone rather than the gallery: the
          // receipt is in the partner's hand, not on their device.
          capture="environment"
          className="sr-only"
          aria-label={t('finance.expense.photo')}
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) void handlePhoto(file);
          }}
        />
        <button
          type="button"
          onClick={() => fileInput.current?.click()}
          className="min-h-touch w-full rounded-xl border border-dashed border-gray-400 bg-white px-4 py-3 text-gray-700"
        >
          {isUploading
            ? t('finance.expense.uploading')
            : receiptId !== null
              ? t('finance.expense.photoAttached')
              : t('finance.expense.photo')}
        </button>
        {photo !== null && receiptId === null && !isUploading && (
          <button
            type="button"
            onClick={() => void handlePhoto(photo)}
            className="mt-2 min-h-touch w-full rounded-xl bg-amber-50 px-4 py-2 text-sm text-amber-900"
          >
            {t('app.retry')}
          </button>
        )}
      </div>

      <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="note">
        {t('finance.expense.note')}
      </label>
      <input
        id="note"
        value={note}
        onChange={(event) => setNote(event.target.value)}
        maxLength={1000}
        className="mb-5 min-h-touch w-full rounded-lg border border-gray-300 px-4 py-3"
      />

      {errorKey !== null && (
        <div role="alert" className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-800">
          <p>{t(errorKey)}</p>
          {/*
            "Check the marked fields" is useless when the field the server
            rejected has no box on this form — txnDate is set from the clock,
            not typed, and a partner shown an unmarked form has nothing to
            correct and no way to report it. Anything not rendered inline is
            listed here instead.
          */}
          {Object.entries(fieldErrors)
            .filter(([field]) => !INLINE_FIELD_ERRORS.includes(field))
            .map(([field, message]) => (
              <p key={field} className="mt-1">
                {message}
              </p>
            ))}
        </div>
      )}

      {savedId !== null && errorKey === null && (
        <p role="status" className="mb-4 rounded-lg bg-green-50 px-4 py-3 text-sm text-green-800">
          {t('finance.expense.saved')}
        </p>
      )}

      <button
        type="submit"
        disabled={!canSubmit}
        className="min-h-[52px] w-full rounded-xl bg-green-700 text-lg font-medium text-white disabled:opacity-50"
      >
        {t(createTransaction.isPending ? 'finance.expense.saving' : 'finance.expense.submit')}
      </button>
    </form>
  );
}
